import { useContext, useMemo } from 'react';
import DropDownMultiple from '../../../../../Components/DropDownMultiple';
import { useParams } from 'react-router-dom';
import { UnitsContext } from '../../../../../Contexts/UnitsContext';
import { MuiChipsInput } from 'mui-chips-input';
import {
	EXPB_DOC_CONSTANTS,
	AB_SPLIT_TEST_CONSTANTS,
	SPLIT_CONFIG_TYPES,
	CONFIG_KEYDATA_CONSTANTS,
	MULTI_SPLIT_TEST_CONSTANTS,
	SECTION_TYPES
} from '../../../../../core/constants';
import { Modalv2Context } from '../../../../../Contexts/Modalv2Context';
import { toast } from 'react-toastify';
import { createExpbDocForSite, updateExpbDocForSite } from '../../../../../api/expbDoc';
import { getInitialAbSplitTestConfig } from '../../../../../utilities/initialConfigs';
import { UserContext } from '../../../../../Contexts/UserContext';
import { v4 as uuid } from 'uuid';
import commonStyles from '../../../../../commonStyles';
import PageHeading from '../../../../../Components/PageHeading';
//mui imports====
import { Grid, Paper, Button, Box, Alert } from '@mui/material';
import { getJsonDiff, handleFramerateSiteDocUpdation } from '../../../../../utilities/helpers';
import { useNavigate } from 'react-router-dom';
import { FEATURE_DESCRIPTION } from '../../../constant';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReactJson from 'react-json-view';
import GeneralTab from '../../../../../Components/GeneralTabAndTabContent';
import { ExperimentSection } from './ExperimentSection';
import { SelectiveRolloutExperimentSection } from './SelectiveRolloutExperimentSection';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';

const {
	BLOCKERS: { SITE_SYNC, SITE_UPDATE },
	CONFIG_EDIT_MODES
} = EXPB_DOC_CONSTANTS;

const {
	IDENTIFIER,
	CUSTOM_TARGETING: { KEY, CONTROL_VALUE, EXPERIMENT_VALUE },
	AB_SPLIT_TYPE: { CONTROL, EXPERIMENT }
} = AB_SPLIT_TEST_CONSTANTS;

const { EXPERIMENT_SPLIT, MULTI_SPLIT } = SPLIT_CONFIG_TYPES;

const { CUSTOM_TARGETING_KEYDATA, MAPPED_PLAYLIST_DATA, SEC_PV_TARGETING, PAGEVIEW_PREFIX } =
	CONFIG_KEYDATA_CONSTANTS;

const { EXPERIMENT_SECTION, CONSUMER_CONFIG_SECTION } = SECTION_TYPES;
// TODO - Remove this constant after testing

/**
 * @name ExperimentsContextSettings
 * @description This is the ui entry point for the experiments context ui
 */
const ExperimentsContextSettings = () => {
	const {
		units,
		mode,
		allValidSiteTags,
		getFinalOverviewObj,
		previousCouchBaseConfig,
		setPreviousCouchBaseConfig,
		initializeConfigData,
		curSelectedUnit,
		updateUnits,
		expbDoc,
		updateExpbDoc,
		userHasExpbDocEditAccess,
		expbDocEditMode,
		previousExpbDoc,
		allAccessEmails,
		updateExpbEditMode,
		sectionLevelSplit,
		updateSectionLevelSplit,
		nonArchivedUnitsArray,
		updateNonArchivedUnitsArray
	} = useContext(UnitsContext);
	const { updateModalContent } = useContext(Modalv2Context);
	const { user } = useContext(UserContext);
	const { siteRawConfig } = useContext(UnitsContext);
	const navigate = useNavigate();
	const blockersArray = (expbDoc && expbDoc.blockers) || [];
	const additionalSiteAccessEmails = (expbDoc && expbDoc.additionalSiteAccessEmails) || [];
	const multiSplitTestDataKey = MULTI_SPLIT_TEST_CONSTANTS.IDENTIFIER;
	const customTargetingKvpKeyData = `${CUSTOM_TARGETING_KEYDATA}.targeting`;
	const customPageTargetingKvpKeyDataEnabled = `${CUSTOM_TARGETING_KEYDATA}.pageviewTargeting.enabled`;
	const customPageSecPvTargeting = `${CUSTOM_TARGETING_KEYDATA}.pageviewTargeting.${SEC_PV_TARGETING}`;
	const mappedPlaylistDataEnabledKeyData = `${MAPPED_PLAYLIST_DATA}.autoRemap.enabled`;
	const syncSiteDisabled = expbDoc && expbDoc.blockers.includes(SITE_SYNC);
	const { SITE_CONFIG } = FEATURE_DESCRIPTION;

	const isAutoRemapEnabled = useMemo(
		() => Object.values(units).some((unit) => unit[mappedPlaylistDataEnabledKeyData] === true),
		[units]
	);

	const { siteId } = useParams();
	const commonStyleClasses = commonStyles();
	/**
	 * @name updateBlockers
	 * @description This function updates the blockers parameter for the expb config
	 * @param {string[]} updatedBlockersArray
	 *  @returns {void}
	 */
	const updateBlockers = (updatedBlockersArray) => {
		updateExpbDoc((prevExpbDoc) => {
			const clonedExpbDoc = structuredClone(prevExpbDoc);
			clonedExpbDoc.blockers = updatedBlockersArray;
			return clonedExpbDoc;
		});
	};

	/**
	 * @name updateAccessEmails
	 * @description This function updates the additional access emails in the expb config
	 * @param {string[]} updatedAccessEmailsArray
	 * @returns {void}
	 */
	const updateAccessEmails = (updatedAccessEmailsArray) => {
		updateExpbDoc((prevExpbDoc) => {
			const clonedExpbDoc = structuredClone(prevExpbDoc);
			clonedExpbDoc.additionalSiteAccessEmails = updatedAccessEmailsArray;
			return clonedExpbDoc;
		});
	};

	/**
	 * @name generateSectionLevelSplit
	 * @description This function generates the section level split for a/b split experiment
	 * @param {string} controlSectionRef
	 * @param {string} experimentSectionRef
	 * @returns {void}
	 */
	const generateSectionLevelSplit = (controlSectionRef, experimentSectionRef) => {
		const splitConfigClone = structuredClone(sectionLevelSplit);
		const controlVideoSectionId = units[controlSectionRef].videoSectionId;

		let splitPercentage = 0;
		// If controlVideoSectionId is present in SectionLevelSpit then divide the splitPercentage b/w them
		if (sectionLevelSplit && splitConfigClone.splits[controlVideoSectionId]) {
			const videoSectionSplitPercentage =
				splitConfigClone.splits[controlVideoSectionId].splitPercentage;
			if (videoSectionSplitPercentage !== 0) {
				splitPercentage = videoSectionSplitPercentage / 2;
			}
		}

		splitConfigClone.enabled = true;
		splitConfigClone.splits[controlVideoSectionId] = { type: EXPERIMENT_SPLIT };
		splitConfigClone.splits[controlVideoSectionId].controlExperimentSplits = {
			[controlSectionRef]: { splitPercentage },
			[experimentSectionRef]: { splitPercentage }
		};

		updateSectionLevelSplit(splitConfigClone);
	};

	/**
	 * @name generateCustomTargetingKey
	 * @description Generates the custom targeting key for control or experiment sections
	 *
	 * @param {boolean} isControlSection - Whether the section is control
	 * @param {number} abSplitTestKvpVersion - Version of the AB split test KVP
	 * @param {boolean} [isMultiSplit=false] - Whether this is a multi-split test
	 * @param {number|null} [index=null] - Index of the experiment in multi-split
	 * @returns {string} Generated custom targeting key.
	 */
	const generateCustomTargetingKey = (
		isControlSection,
		abSplitTestKvpVersion,
		isMultiSplit = false,
		index = null
	) => {
		const prefix = isControlSection ? CONTROL_VALUE : EXPERIMENT_VALUE;

		if (isMultiSplit && index !== null) {
			// For ( Section Level Split and Multi Split)
			return `${EXPERIMENT_VALUE}_${index}_${abSplitTestKvpVersion}`;
		}

		// For ( SiteLevelSplit and Experiment Split) and ( Section Level Split and ExperimentSplit)
		return `${prefix}_${abSplitTestKvpVersion}`;
	};
	/**
	 * @name addCustomTargetingToAbSplitConfig
	 * @description This function adds the custom targeting to the control and experiment section configs
	 * @param {object} clonedSectionConfig
	 * @param {string} abSplitTestKvpVersion
	 * @returns {object} sectionConfig
	 */
	const addCustomTargetingToAbSplitConfig = (clonedSectionConfig, abSplitTestKvpVersion) => {
		// Checking if cloned section is CONTROL or EXPERIMENT.
		const isControlSection = clonedSectionConfig[IDENTIFIER].splitType === CONTROL;
		const customTargettingKv = generateCustomTargetingKey(isControlSection, abSplitTestKvpVersion);

		clonedSectionConfig = {
			...clonedSectionConfig,
			[customTargetingKvpKeyData]: {
				...clonedSectionConfig[customTargetingKvpKeyData],
				[KEY]: customTargettingKv
			},
			[customPageTargetingKvpKeyDataEnabled]: true,
			[customPageSecPvTargeting]: customTargettingKv + PAGEVIEW_PREFIX
		};

		return clonedSectionConfig;
	};
	/**
	 * @name addCustomTargettingToMultiSplitConfig
	 * @description This function adds the custom targeting to the control and experiment section configs For Multi Split Section
	 * @param {object} clonedSectionConfig
	 * @param {string} abSplitTestKvpVersion
	 * @param {number | null} index
	 * @returns {object} sectionConfig
	 */
	const addCustomTargettingToMultiSplitConfig = (
		clonedSectionConfig,
		abSplitTestKvpVersion,
		index
	) => {
		// Checking if cloned section is CONTROL or EXPERIMENT.
		const isControlSection = clonedSectionConfig[multiSplitTestDataKey].splitType === CONTROL;
		const customTargettingKv = isControlSection
			? generateCustomTargetingKey(isControlSection, abSplitTestKvpVersion)
			: generateCustomTargetingKey(isControlSection, abSplitTestKvpVersion, true, index);

		clonedSectionConfig = {
			...clonedSectionConfig,
			[customTargetingKvpKeyData]: {
				...clonedSectionConfig[customTargetingKvpKeyData],
				[KEY]: customTargettingKv
			},
			[customPageTargetingKvpKeyDataEnabled]: true,
			[customPageSecPvTargeting]: `${customTargettingKv}_pv`
		};

		return clonedSectionConfig;
	};

	/**
	 * @name addAbSplitTestDataToConfig
	 * @description This function adds the abSplitTestData to the control and experiment section configs
	 * @param {string} controlSectionRef
	 * @param {string} expSectionName
	 * @param {string} abSplitTestKvpVersion
	 */
	const addAbSplitTestDataToConfig = (controlSectionRef, expSectionName, abSplitTestKvpVersion) => {
		const experimentSectionRef = uuid().toString();

		const controlAbSplitTestData = getInitialAbSplitTestConfig(CONTROL, experimentSectionRef);
		const experimentAbSplitTestData = getInitialAbSplitTestConfig(EXPERIMENT, controlSectionRef);

		// Setting up control section for abSplitTestData and custom targeting
		let clonedControlSectionConfig = structuredClone(units[controlSectionRef]);
		clonedControlSectionConfig[IDENTIFIER] = controlAbSplitTestData;
		clonedControlSectionConfig = addCustomTargetingToAbSplitConfig(
			clonedControlSectionConfig,
			abSplitTestKvpVersion
		);

		// Setting up new experiment section for abSplitTestData and custom targeting
		let clonedExperimentSectionConfig = structuredClone(units[controlSectionRef]);
		clonedExperimentSectionConfig.name = expSectionName;
		clonedExperimentSectionConfig.sectionRef = experimentSectionRef;
		clonedExperimentSectionConfig.videoSectionId = units[controlSectionRef].videoSectionId;
		clonedExperimentSectionConfig[IDENTIFIER] = experimentAbSplitTestData;
		clonedExperimentSectionConfig = addCustomTargetingToAbSplitConfig(
			clonedExperimentSectionConfig,
			abSplitTestKvpVersion
		);

		generateSectionLevelSplit(controlSectionRef, experimentSectionRef);

		updateUnits({
			...units,
			[experimentSectionRef]: clonedExperimentSectionConfig,
			[controlSectionRef]: clonedControlSectionConfig
		});
		updateNonArchivedUnitsArray([...nonArchivedUnitsArray, experimentSectionRef]);
	};

	const generateControlAndExperimentSplit = (controlSectionRef, expSectionName, numberOfSplit) => {
		try {
			// Clone the control section and initialize the control split data
			const controlSection = units[controlSectionRef];
			const clonedControlSectionConfig = structuredClone(controlSection);
			const controlSplitData = { splitType: CONTROL, experimentalRefs: [] };

			// Attach the split data
			clonedControlSectionConfig[multiSplitTestDataKey] = controlSplitData;

			const experimentSectionConfigs = [];

			// Generate experiment sections using a for loop
			for (let i = 0; i < numberOfSplit; i++) {
				const experimentSectionConfig = structuredClone(controlSection);
				const experimentSectionRef = uuid().toString();
				const experimentSplitData = {
					splitType: EXPERIMENT,
					controlSectionRef,
					index: i + 1
				};

				experimentSectionConfig.sectionRef = experimentSectionRef;
				experimentSectionConfig.videoSectionId = controlSection.videoSectionId;
				experimentSectionConfig[multiSplitTestDataKey] = experimentSplitData;
				experimentSectionConfig.name = expSectionName;

				// Maintain multiExperimentConfig
				experimentSectionConfigs.push(experimentSectionConfig);
			}

			// Return both control and experiment configurations
			return {
				controlSectionConfig: clonedControlSectionConfig,
				experimentSectionConfigs
			};
		} catch (error) {
			throw new Error(
				`Error in generating splits for control section ${controlSectionRef}: ${error.message}`
			);
		}
	};

	/**
	 * @name addMultiSplitTestDataToConfig
	 * @description Adds multi-split test data to the control and experiment section configs.
	 * @param {string} controlSectionRef - Reference to the control section.
	 * @param {string} expSectionName - Name of the experiment sections.
	 * @param {number} numberOfSplit - Number of experiment splits.
	 * @param {string} abSplitTestKvpVersion

	 */
	const addMultiSplitTestDataToConfig = (
		controlSectionRef,
		expSectionName,
		numberOfSplit,
		abSplitTestKvpVersion
	) => {
		try {
			if (!controlSectionRef || !expSectionName || !numberOfSplit || numberOfSplit <= 0) {
				throw new Error('Invalid parameters: Ensure valid control section and number of splits.');
			}

			// Clone existing units and section split configurations
			const updatedUnits = { ...units };
			const updatedNonArchivedUnits = [...nonArchivedUnitsArray];
			const splitConfigClone = structuredClone(sectionLevelSplit);
			splitConfigClone.enabled = true;
			const controlVideoSectionId = units[controlSectionRef]?.videoSectionId;

			// TODO:  Need to Check this
			if (!controlVideoSectionId) {
				throw new Error(`Control section ${controlSectionRef} does not exist.`);
			}

			// Initialize split structure if not present
			if (!splitConfigClone.splits[controlVideoSectionId]) {
				splitConfigClone.splits[controlVideoSectionId] = {
					type: MULTI_SPLIT,
					controlExperimentSplits: {}
				};
			}

			// Generate experiment sections
			let { controlSectionConfig, experimentSectionConfigs } = generateControlAndExperimentSplit(
				controlSectionRef,
				expSectionName,
				numberOfSplit
			);
			// Adding Custom_Targetting on Control Section
			controlSectionConfig = addCustomTargettingToMultiSplitConfig(
				controlSectionConfig,
				abSplitTestKvpVersion
			);

			// updating Control Section
			updatedUnits[controlSectionRef] = controlSectionConfig;
			splitConfigClone.splits[controlVideoSectionId].controlExperimentSplits[controlSectionRef] = {
				splitPercentage: 0
			};

			//  Update units and split config for experiment section
			experimentSectionConfigs.map((experimentSectionConfig, index) => {
				const sectionRef = experimentSectionConfig.sectionRef;
				const currentIndex = index + 1;
				// Adding Custom Targetting on All Experiment Sections
				experimentSectionConfig = addCustomTargettingToMultiSplitConfig(
					experimentSectionConfig,
					abSplitTestKvpVersion,
					currentIndex
				);

				updatedUnits[sectionRef] = experimentSectionConfig;
				updatedUnits[controlSectionRef][multiSplitTestDataKey].experimentalRefs.push(sectionRef);
				updatedNonArchivedUnits.push(sectionRef);

				splitConfigClone.splits[controlVideoSectionId].controlExperimentSplits[sectionRef] = {
					splitPercentage: 0
				};
				return experimentSectionConfig;
			});

			// Update global state
			updateUnits(updatedUnits);
			updateSectionLevelSplit(splitConfigClone);
			updateNonArchivedUnitsArray(updatedNonArchivedUnits);
		} catch (error) {
			console.error('Error in addMultiSplitTestDataToConfig:', error.message);
			toast.error(error.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	/**
	 * @name addNewExperiment
	 * @description This function adds a new Experiment to the experiments list of expb config
	 * @param {string} experimentName
	 * @param {string} email
	 * @param {string} controlSectionRef
	 * @param {string} expSectionName
	 * @param {string} abSplitTestKvpVersion
	 * @param {boolean} multiSplit
	 * @param {number} numberOfSplit
	 * @returns {void}
	 */
	const addNewExperiment = (
		experimentName,
		email,
		controlSectionRef,
		expSectionName,
		abSplitTestKvpVersion,
		multiSplit,
		numberOfSplit,
		startedAt
	) => {
		// If Input data is Missing, throws an error.
		if (
			!experimentName ||
			!email ||
			!controlSectionRef ||
			!expSectionName ||
			!abSplitTestKvpVersion ||
			!startedAt
		) {
			toast.error('Some Fields are Missing', { position: toast.POSITION.BOTTOM_RIGHT });
			return;
		}

		// We explicitly exclude the controlSectionRef from the uniqueness check because
		// we intentionally force the Exp Section Name to exactly mirror the Control Section's name
		// to prevent naming mismatches.
		const doesExpSectionNameAlreadyExists = Object.keys(units).find(
			(sectionRef) => units[sectionRef].name === expSectionName && sectionRef !== controlSectionRef
		);
		// If Experiment Section Name already exists in an unrelated section, throws an error.
		if (doesExpSectionNameAlreadyExists) {
			return toast.error('Experiment Section Name already exists!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}

		const clonedExpbDoc = structuredClone(expbDoc);
		const allExperimentsArray = clonedExpbDoc.experiments;

		const newExperimentObj = {
			email,
			status: 'PAUSED',
			experimentName,
			contextUrl: '',
			comment: '',
			controlVideoSectionId: units[controlSectionRef].videoSectionId,
			multiSplit,
			startedAt
		};

		allExperimentsArray.push(newExperimentObj);
		updateExpbDoc(clonedExpbDoc);

		if (multiSplit) {
			addMultiSplitTestDataToConfig(
				controlSectionRef,
				expSectionName,
				numberOfSplit,
				abSplitTestKvpVersion
			);
		} else {
			addAbSplitTestDataToConfig(controlSectionRef, expSectionName, abSplitTestKvpVersion);
		}

		updateModalContent(null);
	};

	/**
	 * @name shouldUpdateSite
	 * @description Checks if site update is needed based on experiment changes
	 * @param {Object} prevExpbDoc - Previous expb doc
	 * @param {Object} currentExpbDoc - Current expb doc
	 * @returns {boolean} - Whether site update is needed
	 */
	const shouldUpdateSite = (prevExpbDoc, currentExpbDoc) => {
		const expbDiff = getJsonDiff(prevExpbDoc, currentExpbDoc);

		if (!expbDiff.experiments) {
			return false;
		}

		/**
		 * expbDiff.experiments can be either an array (when there are multiple experiment changes)
		 * or a single object (when only one experiment has changed). To simplify downstream logic,
		 * we always convert it to an array so that subsequent code can iterate over it.
		 */
		const experimentsDiff = Array.isArray(expbDiff.experiments)
			? expbDiff.experiments
			: [expbDiff.experiments];

		const hasNewExperiments = experimentsDiff.some(
			(exp) =>
				!prevExpbDoc.experiments.find((prevExp) => prevExp.experimentName === exp.experimentName)
		);
		const hasConcludedExperiments = currentExpbDoc?.experiments?.some((exp) => {
			const prevExp = prevExpbDoc.experiments.find((p) => p.experimentName === exp.experimentName);
			return prevExp && prevExp.status !== 'CONCLUDED' && exp.status === 'CONCLUDED';
		});

		return hasNewExperiments || hasConcludedExperiments;
	};

	/**
	 * @name siteDocUpdateHandler
	 * @description Handles the update of the site document by invoking the helper function `handleFramerateSiteDocUpdation`.
	 */
	const siteDocUpdateHandler = async () => {
		try {
			const overviewObject = getFinalOverviewObj();
			const data = await handleFramerateSiteDocUpdation({
				mode,
				overviewObject,
				previousCouchBaseConfig,
				allValidSiteTags,
				initializeConfigData,
				curSelectedUnit,
				setPreviousCouchBaseConfig,
				shouldValidateChanges: false // Skip change validation for experiment updates
			});

			toast.success(data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
			if (mode === 'CREATE') {
				navigate(`/update-config/${overviewObject?.siteId}`);
			}
		} catch (err) {
			const errorMessage = err?.response?.data?.data?.message || err?.message;
			toast.error(errorMessage, { position: toast.POSITION.BOTTOM_RIGHT });
			throw err; // Re-throw the error so handleButtonClick can catch it
		}
	};

	/**
	 * @name handleButtonClick
	 * @description This function is triggered when user clicks the Create or Update button for\
	 * the expb config, and it creates/updates the site expb config accordingly
	 */
	const handleButtonClick = async () => {
		try {
			if (expbDoc.additionalSiteAccessEmails.length === 0) {
				throw new Error('At least 1 email should be present in additional access emails list.');
			}
			if (!allAccessEmails.includes(user.email)) {
				throw new Error(
					'User must have atleast 1 experiment or email must be added in additional access emails list'
				);
			}
			const invalidExperiment = expbDoc.experiments?.find(
				(exp) => !exp.comment?.trim() || !exp.contextUrl?.trim()
			);

			if (invalidExperiment) {
				throw new Error('Comment or Context URL is missing in the experiment details.');
			}

			const payload = {
				siteId,
				data: expbDoc,
				previousCouchBaseConfig: previousExpbDoc.current,
				mode: expbDocEditMode
			};

			let response;
			if (expbDocEditMode === CONFIG_EDIT_MODES.CREATE) {
				// Adding siteId and siteDomain to the payload
				payload.data.siteId = siteId;
				payload.data.siteDomain = siteRawConfig?.siteDomain;

				if (shouldUpdateSite(previousExpbDoc.current, expbDoc)) {
					try {
						await siteDocUpdateHandler();
					} catch (err) {
						// If siteDocUpdateHandler fails, do not proceed
						return;
					}
				}
				response = await createExpbDocForSite(payload);

				updateExpbEditMode(CONFIG_EDIT_MODES.UPDATE);
			} else if (expbDocEditMode === CONFIG_EDIT_MODES.UPDATE) {
				if (shouldUpdateSite(previousExpbDoc.current, expbDoc)) {
					try {
						await siteDocUpdateHandler();
					} catch (err) {
						// If siteDocUpdateHandler fails, do not proceed
						return;
					}
				}
				response = await updateExpbDocForSite(payload);
			} else {
				throw new Error(`Edit Mode issue: ${expbDocEditMode}`);
			}

			previousExpbDoc.current = expbDoc;
			toast.success(response?.data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		} catch (err) {
			const requestError = err?.response?.data?.data?.message;
			toast.error(requestError || err.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	if (mode === 'CREATE') {
		return (
			<Paper style={{ textAlign: 'center', padding: '5rem' }}>
				Can't Create expb doc during Site Creation
			</Paper>
		);
	}

	const tabListAndPanels = [
		{
			tab: <Tab label="Experiment Config" value={EXPERIMENT_SECTION} key={EXPERIMENT_SECTION} />,
			tabPanel: (
				<TabPanel
					value={EXPERIMENT_SECTION}
					key={EXPERIMENT_SECTION}
					sx={{ padding: 0, marginTop: '1.5rem', width: '100%' }}
				>
					<ExperimentSection
						expbDoc={expbDoc}
						userHasExpbDocEditAccess={userHasExpbDocEditAccess}
						addNewExperiment={addNewExperiment}
						units={units}
					/>
				</TabPanel>
			)
		},
		{
			tab: (
				<Tab
					label="Consumer Config"
					value={CONSUMER_CONFIG_SECTION}
					key={CONSUMER_CONFIG_SECTION}
				/>
			),
			tabPanel: (
				<TabPanel
					value={CONSUMER_CONFIG_SECTION}
					key={CONSUMER_CONFIG_SECTION}
					sx={{ padding: 0, marginTop: '1.5rem', width: '100%' }}
				>
					<SelectiveRolloutExperimentSection expbDoc={expbDoc} updateExpbDoc={updateExpbDoc} />
				</TabPanel>
			)
		}
	];

	return (
		<>
			<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
				{expbDoc && (
					<>
						<PageHeading
							headingName="Experiments Context"
							headingPath={['Dashboard', 'Framerate', 'Experiment Context']}
							description="Manage A/B Split Test Experiments"
							showInfoButton={true}
							infoMessage={SITE_CONFIG.EXPERIMENTS_CONTEXT}
						/>
						{syncSiteDisabled && isAutoRemapEnabled && (
							<Alert sx={{ width: '90%', margin: '2rem auto' }} severity="warning">
								Site Sync is currently disabled. As a result, videos added through auto-remap will
								not be automatically reflected on the live site.
							</Alert>
						)}
						<Grid
							container
							spacing={2}
							style={{
								width: '95%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								margin: '2rem 0 0 4rem'
							}}
						>
							<Grid item xs={2}>
								<DropDownMultiple
									isObjectEditor={false}
									directValue={blockersArray}
									updateDirectValue={updateBlockers}
									list={[SITE_UPDATE, SITE_SYNC]}
									label="Site Feature Blockers"
									disabled={!userHasExpbDocEditAccess}
								/>
							</Grid>

							<Grid item xs={8} style={{ width: '7rem' }}>
								<MuiChipsInput
									label="Additional Access Emails"
									color="primary"
									value={additionalSiteAccessEmails}
									onChange={updateAccessEmails}
									sx={{ width: '100%' }}
									disabled={!userHasExpbDocEditAccess}
								/>
							</Grid>

							<Grid item xs={2} style={{ display: 'flex', alignItems: 'center' }}>
								<Button
									onClick={handleButtonClick}
									variant="contained"
									disabled={!userHasExpbDocEditAccess}
								>
									{expbDocEditMode === CONFIG_EDIT_MODES.CREATE
										? 'Create Expb Doc'
										: 'Update Expb Doc'}
								</Button>
							</Grid>
						</Grid>
					</>
				)}

				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						marginTop: '3rem'
					}}
				>
					<GeneralTab
						tabListAndPanels={tabListAndPanels}
						tabListStyles={{ width: 'fit-content', marginLeft: '3rem' }}
						tabListProps={{
							variant: 'scrollable',
							visibleScrollbar: true,
							scrollButtons: 'auto',
							sx: {
								'& .MuiTabs-scrollableX': {
									'&::-webkit-scrollbar': {
										height: '10px'
									}
								}
							}
						}}
					/>
				</Box>

				<VisibilityIcon
					onClick={() =>
						updateModalContent(
							<Box className={commonStyleClasses.configOverview}>
								<ReactJson
									enableClipboard={true}
									displayDataTypes={false}
									src={expbDoc}
									name={false}
								/>
							</Box>
						)
					}
					sx={{
						position: 'fixed',
						bottom: 16,
						right: 10,
						width: 60,
						height: 60,
						background: '#f44336',
						borderRadius: '50%',
						padding: '12px',
						color: '#fff',
						'&:hover': {
							background: '#d32f2f',
							transform: 'scale(1.1)'
						},
						boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
						zIndex: 9999
					}}
				/>
			</Paper>
		</>
	);
};

export default ExperimentsContextSettings;
